<template>
  <teleport to="body">
    <div
      v-if="visible"
      class="sr-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sr-modal-title"
      @click.self="emit('close')"
    >
      <div class="sr-modal">
        <header class="sr-modal-header">
          <div class="sr-modal-heading">
            <p class="sr-modal-eyebrow">Results</p>
            <h3 id="sr-modal-title" class="sr-modal-title">{{ campaignName || 'Selected campaign' }}</h3>
          </div>
          <button
            type="button"
            class="icon-close-button"
            aria-label="Close"
            @click="emit('close')"
          >
            ×
          </button>
        </header>

        <div class="sr-modal-body">
          <SurveyResponsesPanel
            :campaign-id="campaignId"
            :campaign-name="campaignName"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import SurveyResponsesPanel from '@/components/SurveyResponsesPanel.vue'

defineProps<{
  visible: boolean
  campaignId: string | null
  campaignName?: string | null
}>()

const emit = defineEmits<{ (e: 'close'): void }>()
</script>

<style scoped>
.sr-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

/* Two-tone to match the Campaign Insights panel: the outer surface is the
   blue-grey card colour (--ui-inverse-surface, #2c3e50); the table inside uses
   the darker blue tile colour (see .srp-table-scroll in SurveyResponsesPanel). */
.sr-modal {
  background: var(--ui-inverse-surface, #2c3e50);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  width: min(1080px, 100%);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  color: var(--vt-c-white, #ffffff);
  overflow: hidden;
}

.sr-modal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.1rem 3.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.sr-modal-heading {
  text-align: center;
}

.sr-modal-eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.6;
}

.sr-modal-title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 800;
  line-height: 1.15;
  word-break: break-word;
}

.icon-close-button {
  position: absolute;
  top: 0.9rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  min-width: 32px;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--vt-c-accent-light);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.8;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s ease, opacity 0.12s ease, color 0.12s ease;
}

.icon-close-button:hover {
  opacity: 1;
  background: rgba(150, 255, 201, 0.12);
  color: var(--vt-c-accent);
  transform: none;
  box-shadow: none;
}

.sr-modal-body {
  padding: 1rem 1.4rem 1.2rem;
  overflow: auto;
  flex: 1 1 auto;
}
</style>
